/**
 * The name in the header, on the origin that has no way to ask for it.
 *
 * **A second cookie, deliberately, and the separation is the design.**
 * `session-hint.ts` beside this file carries four booleans, and it argues at
 * length that forging it buys an attacker the word "Account" in their own
 * header and nothing else. That argument is the whole licence for the
 * storefront to draw a personalised bar without acquiring an identity layer,
 * and it holds only while the hint says nothing about *who* anybody is. A name
 * folded into `gb_signed_in` would end it in one line, and the file that would
 * have to be rewritten is the one every reviewer of this mechanism reads first.
 *
 * So #113 gave the name its own cookie, its own name and its own contract.
 * `gb_signed_in` is still four booleans and `session-hint.test.ts` still pins
 * its value to `/^1[a-z]*$/`; `gb_name` is a string a person chose, labelled as
 * exactly that, and separable — a browser, a proxy or a reader can drop one
 * without touching the other.
 *
 * **What #113 was about.** The React app draws the person's name in the bar
 * (`Account` in `apps/app/src/router.tsx`: on a shared machine, which of two
 * people is signed in is the thing worth reading). The storefront could not.
 * `SiteNav.account` takes a React node, markup written in an `.astro` file is
 * not one, and passing one anyway answered every page on the site with a 200
 * and an empty body — the failure the storefront's render test
 * exists to pin shut. So the store fell through to the literal word "Account",
 * and the two bars disagreed on the one entry a person reads to check they are
 * themselves.
 *
 * **This is not a credential either, and it is the same argument pointing at a
 * different value.** A browser writes it, so anybody can write anything into
 * it. Nothing authorises on it, nothing is looked up by it, and the worst a
 * forged value buys is the wrong word in the corner of your own header. The
 * moment anything believes it — a database lookup, a greeting in an email, a
 * log line attributed to it — that stops being true, and this file has to be
 * reconsidered rather than extended.
 *
 * **The storefront reads it only when the hint says somebody is signed in**,
 * and that ordering is load-bearing rather than tidy. The two cookies are
 * written and cleared together, but a browser is free to keep one and drop the
 * other — a partial clear, an extension, a profile copied between machines —
 * and a `gb_name` surviving a cleared `gb_signed_in` would greet a stranger by
 * the previous visitor's name on a machine they had signed out of. Gating the
 * read on the hint makes that state unreachable rather than unlikely; see
 * the app's own `Shell`, which is the only place that writes or reads it.
 *
 * **It carries a name and never an address.** An email address identifies an
 * account across every site that has ever seen it, which is a different
 * proposition from a display name somebody chose to be called by in public, and
 * this cookie must never be handed one. `displayName` below is what both
 * writers pass their value through, so there is one place that decides what a
 * name is.
 */

import {
  sessionHintDomain,
  SESSION_HINT_LIFETIME_SECONDS,
  type HintLocation,
} from './session-hint';

/**
 * The cookie's name.
 *
 * Says what it holds, in the same voice as `gb_signed_in` and for the same
 * reason: whoever finds this in a browser inspector should be able to tell from
 * the name alone that it is a label rather than a session. `gb_user`, `gb_id`
 * or `gb_account` would each suggest the opposite of what is true here.
 */
export const DISPLAY_NAME_COOKIE = 'gb_name';

/**
 * The longest name the bar will carry, in code points.
 *
 * Two limits meet here and the smaller of them is a reader's attention. A
 * cookie header is capped around four kilobytes and a percent-escaped
 * non-ASCII name costs up to nine bytes a character, so a few hundred would
 * already be impolite to every request this browser makes; and a navigation bar
 * has room for a word or two, so a name past this point is not being read
 * anyway.
 *
 * Counted in code points rather than in UTF-16 units, which is why the cap is
 * applied through `Array.from` below: slicing a JavaScript string at a fixed
 * index can cut an astral character — an emoji, or several scripts' ordinary
 * letters — in half and leave a lone surrogate, which renders as a replacement
 * box in the one place a person's own name is meant to appear.
 */
export const MAX_DISPLAY_NAME = 64;

/**
 * A name as this system is willing to carry it, or nothing at all.
 *
 * One function, used by every writer and every reader, so that what counts as a
 * name is decided once. The app normalises with it before writing the cookie
 * *and* before drawing its own bar — #113 found the smaller half of the same
 * bug there: `session?.user.name ?? 'Account'` let an account whose name is the
 * empty string render a link with no label at all, which is a target nobody can
 * see and nobody can click. The storefront normalises again on the way in,
 * because what arrives there was written by a browser and is therefore whatever
 * somebody typed into a console.
 *
 * `null` means *no name*, which is a real and ordinary state: an account made
 * through a provider that gave us none, a name that is entirely whitespace, a
 * cookie somebody hand-edited. Every caller answers it by falling back to the
 * word "Account", which is what the bar said before this existed.
 *
 * The three rules, in the order they are applied:
 *
 * — **Whitespace collapses.** A newline in a name is the interesting half of
 *   this: it is legal in a database column, it is meaningless in a bar, and it
 *   is the character a header-injection attempt begins with. Collapsing to
 *   single spaces removes the class rather than the instance.
 * — **A control character refuses the whole name** rather than being stripped
 *   out of it. A name with a NUL, an escape or a C1 byte in it is not a name
 *   somebody chose; it is something addressed to a terminal, a log parser or a
 *   header. Stripping would pass the remainder on as though it were fine, and
 *   the remainder is not what that person is called.
 * — **The cap is applied last**, so a long name is shortened rather than
 *   refused. A name cut short is a poor greeting; a name refused outright is
 *   somebody who cannot find themselves anywhere in their own header.
 */
export function displayName(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;

  const collapsed = value.replace(/\s+/gu, ' ').trim();
  if (collapsed === '') return null;

  // `\s` above has already taken the tabs, the newlines and the form feeds;
  // what is left in this class is NUL, the escape characters and the C1 range,
  // none of which anybody types into a name field on purpose.
  if (/\p{Cc}/u.test(collapsed)) return null;

  const points = Array.from(collapsed);
  return points.length > MAX_DISPLAY_NAME ? points.slice(0, MAX_DISPLAY_NAME).join('') : collapsed;
}

/**
 * The `document.cookie` string that sets or clears the name.
 *
 * Returned rather than assigned, exactly like `sessionHintCookie`: this stays a
 * pure function a test can read, and the app does the one assignment, in
 * `apps/app/src/session-hint.ts`.
 *
 * **Every attribute comes from the hint's own module and none is restated
 * here.** The domain is `sessionHintDomain`, the lifetime is
 * `SESSION_HINT_LIFETIME_SECONDS`, and `Path`, `SameSite` and `Secure` follow
 * the same rules for the same reasons — the note on `sessionHintCookie` argues
 * all three, and not one of those arguments comes out differently for a name.
 * Sharing the source rather than copying the value is what stops the two
 * cookies drifting: two cookies written together but scoped apart would mean a
 * name outliving the hint on some hosts and not others, which is the one
 * failure the middleware's ordering exists to make unreachable, arriving
 * through the back door.
 *
 * `null` clears it, by expiring it in the past rather than writing an empty
 * value — the same note again, and `readDisplayName`, which would otherwise
 * have to know to disbelieve a cookie a browser kept.
 *
 * Percent-encoded, because a name is a string somebody chose and cookie syntax
 * is not prepared for one: a semicolon ends the value, a comma ends it in some
 * parsers, and a space is not a legal cookie octet at all. `O'Neill, Ada` is an
 * ordinary name and an ambiguous cookie. `encodeURIComponent` leaves nothing
 * outside the legal set, and `readDisplayName` is the only thing that undoes
 * it.
 */
export function displayNameCookie(name: string | null, location: HintLocation): string {
  const carried = displayName(name);

  const attributes = [
    `${DISPLAY_NAME_COOKIE}=${carried === null ? '' : encodeURIComponent(carried)}`,
    'Path=/',
    'SameSite=Lax',
    `Max-Age=${carried === null ? 0 : SESSION_HINT_LIFETIME_SECONDS}`,
  ];

  const domain = sessionHintDomain(location.hostname);
  if (domain) attributes.push(`Domain=${domain}`);
  if (location.protocol === 'https:') attributes.push('Secure');

  return attributes.join('; ');
}

/**
 * What a request says this visitor is called, or nothing.
 *
 * Takes the raw `Cookie` header, because the one caller is Astro middleware and
 * that is what it has — the same shape as `readSessionHint`, so the two reads
 * sit beside each other there rather than one of them having to be handed a
 * parsed bag of cookies.
 *
 * **Everything arriving here is untrusted and is normalised again.** The value
 * was written by a browser under a name anybody can spell, so the length cap,
 * the whitespace rule and the control-character refusal all have to hold on the
 * way in as well as on the way out — a header assembled by hand is not bound by
 * what `displayNameCookie` would have written.
 *
 * A value that is not valid percent-encoding is skipped rather than fatal.
 * `decodeURIComponent` throws a `URIError` on a truncated escape — a cookie cut
 * short somewhere in transit, or a bare `%` somebody typed — and a header
 * rendered unreadable by one malformed cookie would take the whole bar with it.
 * Skipping answers "no name", which falls back to the word "Account" and costs
 * nothing else.
 *
 * The first cookie of this name that reads as a name wins, for the reason
 * `readSessionHint` gives: two can exist at once only while a scope is
 * changing, and a browser sends the more specific first.
 */
export function readDisplayName(cookieHeader: string | null | undefined): string | null {
  if (!cookieHeader) return null;

  for (const pair of cookieHeader.split(';')) {
    const separator = pair.indexOf('=');
    if (separator === -1) continue;
    if (pair.slice(0, separator).trim() !== DISPLAY_NAME_COOKIE) continue;

    const raw = pair.slice(separator + 1).trim();
    if (raw === '') continue;

    let decoded: string;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      continue;
    }

    const name = displayName(decoded);
    if (name !== null) return name;
  }

  return null;
}
